import React, { useState, useEffect } from "react";
import {
  Typography,
  Paper,
  Box,
  Button,
  TextField,
  LinearProgress,
  Alert,
  Container,
  Card,
  CardContent,
  InputAdornment,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  Chip,
  Divider,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Security as SecurityIcon,
  Phone as PhoneIcon,
  Key as KeyIcon,
  AccountCircle as AccountIcon,
} from "@mui/icons-material";
import { useSearchParams, useNavigate } from "react-router-dom";
import { medicalProfessionalsService } from "../../services/medicalProfessionalsService";
import { useAuth } from "../../contexts/AuthContext";

const ActivateAccount = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setup2FA, verify2FASetup } = useAuth();

  // Main states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [currentStep, setCurrentStep] = useState(0);
  const [token, setToken] = useState("");

  // Form data
  const [accountData, setAccountData] = useState({
    password: "",
    confirmPassword: "",
  });

  // 2FA setup data
  const [twoFAData, setTwoFAData] = useState({
    phoneNumber: "",
    countryCode: "+1",
    verificationCode: "",
  });

  // UI states
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [setup2FARequired, setSetup2FARequired] = useState(false);
  const [backupCodes, setBackupCodes] = useState([]);
  const [connectionStatus, setConnectionStatus] = useState("checking");

  const steps = ["Set Password", "Setup 2FA", "Complete"];

  useEffect(() => {
    const tokenParam = searchParams.get("token");
    if (!tokenParam) {
      setError(
        "Invalid activation link. Please check your email for the correct link."
      );
    } else {
      setToken(tokenParam);
    }

    // Test connection
  }, [searchParams]);

  const validatePassword = () => {
    if (accountData.password.length < 6) {
      setError("Password must be at least 6 characters long");
      return false;
    }

    if (accountData.password !== accountData.confirmPassword) {
      setError("Passwords do not match");
      return false;
    }

    // Enhanced password validation
    const hasUpperCase = /[A-Z]/.test(accountData.password);
    const hasLowerCase = /[a-z]/.test(accountData.password);
    const hasNumbers = /\d/.test(accountData.password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(accountData.password);

    if (!hasUpperCase || !hasLowerCase || !hasNumbers || !hasSpecialChar) {
      setError(
        "Password must contain uppercase, lowercase, numbers, and special characters"
      );
      return false;
    }

    return true;
  };

  const handlePasswordSetup = async (e) => {
    e.preventDefault();

    if (!validatePassword()) {
      return;
    }

    if (!token) {
      setError("Invalid activation token");
      return;
    }

    setLoading(true);
    setError("");

    try {
      // Activate account with password
      const response = await medicalProfessionalsService.activateAccount(
        token,
        accountData.password
      );

      // Check if 2FA setup is required
      if (response.requires_2fa_setup) {
        setSetup2FARequired(true);
        setCurrentStep(1);
      } else {
        // Activation complete
        setSuccess(
          "Account activated successfully! You can now log in with your credentials."
        );
        setCurrentStep(2);

        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate("/login");
        }, 3000);
      }
    } catch (err) {
      console.error("Error activating account:", err);
      setError(
        err.response?.data?.detail ||
          err.message ||
          "Failed to activate account. Please try again or contact support."
      );
    } finally {
      setLoading(false);
    }
  };

  const handle2FASetup = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      // Setup 2FA with immediate verification if code is provided
      const result = await setup2FA(
        twoFAData.phoneNumber,
        twoFAData.countryCode,
        twoFAData.verificationCode
      );

      if (result.success && !result.requires_verification) {
        // Setup completed immediately
        setBackupCodes(result.backup_codes || []);
        setCurrentStep(2);
        setSuccess("2FA setup completed successfully!");

        // Redirect to login after showing backup codes
        setTimeout(() => {
          navigate("/login");
        }, 5000);
      } else if (result.requires_verification) {
        // Need to verify the code
        setError("Please enter the verification code sent to your phone.");
      } else {
        setError(result.message || "Failed to setup 2FA");
      }
    } catch (err) {
      console.error("2FA setup error:", err);
      setError(err.message || "Failed to setup 2FA");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async () => {
    if (!twoFAData.verificationCode) {
      setError("Please enter the verification code");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const result = await verify2FASetup(twoFAData.verificationCode);

      if (result.success) {
        setBackupCodes(result.backup_codes || []);
        setCurrentStep(2);
        setSuccess("Account activation and 2FA setup completed successfully!");

        // Redirect to login after showing backup codes
        setTimeout(() => {
          navigate("/login");
        }, 5000);
      } else {
        setError(result.message || "Invalid verification code");
      }
    } catch (err) {
      console.error("2FA verification error:", err);
      setError(err.message || "Failed to verify 2FA code");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    if (field === "password" || field === "confirmPassword") {
      setAccountData((prev) => ({ ...prev, [field]: value }));
    } else {
      setTwoFAData((prev) => ({ ...prev, [field]: value }));
    }
    setError(""); // Clear error when user types
  };

  const togglePasswordVisibility = (field) => {
    if (field === "password") {
      setShowPassword(!showPassword);
    } else {
      setShowConfirmPassword(!showConfirmPassword);
    }
  };

  const isPasswordStepValid =
    accountData.password && accountData.confirmPassword;
  const is2FAStepValid = twoFAData.phoneNumber && twoFAData.verificationCode;

  if (loading && currentStep === 0) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <LinearProgress />
        <Typography align="center" sx={{ mt: 2 }}>
          {currentStep === 0
            ? "Activating your account..."
            : "Setting up security..."}
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Card elevation={8} sx={{ borderRadius: 3 }}>
        {/* Header */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #3f51b5 0%, #2196f3 100%)",
            color: "white",
            p: 3,
            textAlign: "center",
          }}
        >
          <AccountIcon sx={{ fontSize: 48, mb: 1 }} />
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            fontWeight="bold"
          >
            Activate Your Account
          </Typography>
          <Typography variant="body1">
            Complete your account setup with enhanced security
          </Typography>
        </Box>

        {/* Progress Stepper */}
        <Box sx={{ p: 3, pb: 1 }}>
          <Stepper activeStep={currentStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        <CardContent sx={{ p: 4 }}>
          {/* Loading Progress */}
          {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

          {/* Connection Status */}
          {connectionStatus === "disconnected" && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              Cannot connect to server. Please ensure the backend is running.
            </Alert>
          )}

          {/* Success Alert */}
          {success && (
            <Alert severity="success" icon={<CheckIcon />} sx={{ mb: 3 }}>
              {success}
            </Alert>
          )}

          {/* Error Alert */}
          {error && (
            <Alert severity="error" icon={<ErrorIcon />} sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Step 0: Password Setup */}
          {currentStep === 0 && !success && (
            <Box component="form" onSubmit={handlePasswordSetup}>
              <Card
                variant="outlined"
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  border: "2px solid",
                  borderColor: "primary.light",
                  bgcolor: "primary.50",
                }}
              >
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <SecurityIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6" color="primary">
                      Create Secure Password
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="textSecondary">
                    Your password must be at least 6 characters and include
                    uppercase, lowercase, numbers, and special characters for
                    HIPAA compliance.
                  </Typography>
                </CardContent>
              </Card>

              <Box mb={3}>
                <TextField
                  fullWidth
                  label="New Password"
                  type={showPassword ? "text" : "password"}
                  value={accountData.password}
                  onChange={(e) =>
                    handleInputChange("password", e.target.value)
                  }
                  required
                  disabled={loading}
                  helperText="Password must contain uppercase, lowercase, numbers, and special characters"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => togglePasswordVisibility("password")}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <Box mb={4}>
                <TextField
                  fullWidth
                  label="Confirm Password"
                  type={showConfirmPassword ? "text" : "password"}
                  value={accountData.confirmPassword}
                  onChange={(e) =>
                    handleInputChange("confirmPassword", e.target.value)
                  }
                  required
                  disabled={loading}
                  helperText="Re-enter your password to confirm"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle confirm password visibility"
                          onClick={() =>
                            togglePasswordVisibility("confirmPassword")
                          }
                          edge="end"
                        >
                          {showConfirmPassword ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                size="large"
                disabled={
                  loading ||
                  !token ||
                  !isPasswordStepValid ||
                  connectionStatus === "disconnected"
                }
                sx={{
                  mb: 2,
                  py: 1.5,
                  fontSize: "1.1rem",
                  fontWeight: 600,
                  borderRadius: 2,
                }}
                startIcon={loading ? <LinearProgress /> : <SecurityIcon />}
              >
                {loading ? "Setting up account..." : "Set Password & Continue"}
              </Button>

              <Typography variant="body2" color="text.secondary" align="center">
                Next: Two-factor authentication setup for enhanced security
              </Typography>
            </Box>
          )}

          {/* Step 1: 2FA Setup */}
          {currentStep === 1 && !success && (
            <Box>
              <Card
                variant="outlined"
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  border: "2px solid",
                  borderColor: "warning.light",
                  bgcolor: "warning.50",
                }}
              >
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <PhoneIcon color="warning" sx={{ mr: 1 }} />
                    <Typography variant="h6" color="warning.main">
                      Setup Two-Factor Authentication
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="textSecondary">
                    For HIPAA compliance, all accounts require two-factor
                    authentication. Enter your phone number to receive SMS
                    verification codes.
                  </Typography>
                </CardContent>
              </Card>

              <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
                <TextField
                  select
                  label="Country"
                  value={twoFAData.countryCode}
                  onChange={(e) =>
                    handleInputChange("countryCode", e.target.value)
                  }
                  sx={{ width: "120px" }}
                  disabled={loading}
                  SelectProps={{ native: true }}
                >
                  <option value="+1">+1 (US)</option>
                  <option value="+44">+44 (UK)</option>
                  <option value="+33">+33 (FR)</option>
                  <option value="+49">+49 (DE)</option>
                  <option value="+81">+81 (JP)</option>
                </TextField>

                <TextField
                  fullWidth
                  label="Phone Number"
                  value={twoFAData.phoneNumber}
                  onChange={(e) =>
                    handleInputChange("phoneNumber", e.target.value)
                  }
                  required
                  disabled={loading}
                  helperText="Enter your phone number for SMS verification"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon color="primary" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <TextField
                fullWidth
                label="SMS Verification Code"
                value={twoFAData.verificationCode}
                onChange={(e) =>
                  handleInputChange(
                    "verificationCode",
                    e.target.value.replace(/\D/g, "")
                  )
                }
                disabled={loading}
                inputProps={{ maxLength: 6 }}
                placeholder="123456"
                helperText="Enter the 6-digit code sent to your phone"
                sx={{ mb: 3 }}
                InputProps={{
                  style: {
                    textAlign: "center",
                    fontSize: "1.2rem",
                    letterSpacing: "0.3rem",
                  },
                }}
              />

              <Box sx={{ display: "flex", gap: 2 }}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handle2FASetup}
                  disabled={
                    loading ||
                    !twoFAData.phoneNumber ||
                    connectionStatus === "disconnected"
                  }
                  sx={{
                    py: 1.5,
                    fontSize: "1rem",
                    fontWeight: 600,
                    borderRadius: 2,
                  }}
                  startIcon={<PhoneIcon />}
                >
                  {loading ? "Sending..." : "Send SMS Code"}
                </Button>

                <Button
                  fullWidth
                  variant="contained"
                  color="success"
                  onClick={handleVerify2FA}
                  disabled={
                    loading ||
                    !is2FAStepValid ||
                    connectionStatus === "disconnected"
                  }
                  sx={{
                    py: 1.5,
                    fontSize: "1rem",
                    fontWeight: 600,
                    borderRadius: 2,
                  }}
                  startIcon={<CheckIcon />}
                >
                  {loading ? "Verifying..." : "Verify & Complete"}
                </Button>
              </Box>

              <Typography
                variant="body2"
                color="text.secondary"
                align="center"
                sx={{ mt: 2 }}
              >
                SMS verification is required for account security
              </Typography>
            </Box>
          )}

          {/* Step 2: Complete */}
          {currentStep === 2 && (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <CheckIcon
                color="success"
                sx={{ fontSize: 80, mb: 2, opacity: 0.9 }}
              />

              <Typography
                variant="h5"
                color="success.main"
                sx={{ mb: 2, fontWeight: 600 }}
              >
                Account Activation Complete!
              </Typography>

              <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
                Your account has been successfully activated with enhanced
                security features.
              </Typography>

              {/* Security features enabled */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  gap: 1,
                  mb: 3,
                  flexWrap: "wrap",
                }}
              >
                <Chip
                  icon={<SecurityIcon />}
                  label="Secure Password"
                  color="success"
                  size="small"
                />
                <Chip
                  icon={<PhoneIcon />}
                  label="SMS 2FA"
                  color="success"
                  size="small"
                />
                <Chip
                  icon={<CheckIcon />}
                  label="HIPAA Compliant"
                  color="primary"
                  size="small"
                />
              </Box>

              {/* Backup codes display */}
              {backupCodes.length > 0 && (
                <Card
                  variant="outlined"
                  sx={{
                    mb: 3,
                    bgcolor: "warning.50",
                    border: "2px solid",
                    borderColor: "warning.light",
                  }}
                >
                  <CardContent>
                    <Typography
                      variant="h6"
                      color="warning.main"
                      sx={{
                        mb: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <KeyIcon sx={{ mr: 1 }} />
                      Backup Recovery Codes
                    </Typography>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ mb: 2 }}
                    >
                      Save these backup codes in a secure location. You can use
                      them to access your account if you lose your phone.
                    </Typography>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(2, 1fr)",
                        gap: 1,
                        fontFamily: "monospace",
                      }}
                    >
                      {backupCodes.map((code, index) => (
                        <Chip
                          key={index}
                          label={code}
                          variant="outlined"
                          sx={{ fontFamily: "monospace", fontSize: "0.9rem" }}
                        />
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              )}

              <Button
                variant="contained"
                size="large"
                onClick={() => navigate("/login")}
                sx={{
                  mb: 2,
                  py: 1.5,
                  fontSize: "1.1rem",
                  fontWeight: 600,
                  borderRadius: 2,
                }}
                startIcon={<AccountIcon />}
              >
                Go to Login
              </Button>

              <Typography variant="body2" color="text.secondary">
                Redirecting to login page automatically...
              </Typography>
            </Box>
          )}

          {/* Already successful but showing final message */}
          {success && currentStep < 2 && (
            <Box textAlign="center" mt={3}>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => navigate("/login")}
                startIcon={<AccountIcon />}
              >
                Go to Login
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Development help */}
      {connectionStatus === "disconnected" && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography
              variant="h6"
              sx={{ mb: 2, display: "flex", alignItems: "center" }}
            >
              <ErrorIcon color="warning" sx={{ mr: 1 }} />
              Connection Issues?
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              If you're having connection issues in development:
            </Typography>
            <Typography variant="body2" component="div">
              • Ensure your backend server is running on port 8000
              <br />
              • Check that CORS is properly configured
              <br />
              • Verify all required environment variables are set
              <br />• Make sure PostgreSQL and Redis are running
            </Typography>
          </CardContent>
        </Card>
      )}
    </Container>
  );
};

export default ActivateAccount;
